<template>
  <div class="w-full">
    <NativeDisplayScheduledReportDisplay
      v-if="selectedReport"
      :report="selectedReport"
      class="mb-8"
    />

    <VideoImpressionsReportDisplay
      v-if="selectedVideoReport"
      :report="selectedVideoReport"
      class="mb-8"
    />

    <h2 class="text-xl font-bold mb-4">Ran Reports</h2>
    <div v-if="error" class="text-red-500 mb-2">{{ error }}</div>

    <div class="overflow-x-auto">
      <table class="min-w-full bg-white table-fixed">
        <thead>
          <tr>
            <th class="px-4 py-2 w-1/5">Campaign</th>
            <th class="px-4 py-2 w-[45%] md:w-[48%] lg:w-[50%]">Filters</th>
            <th class="px-4 py-2 w-[12%] md:w-[10%] lg:w-[8%]">Status</th>
            <th class="px-4 py-2 w-[16%] md:w-[14%] lg:w-[12%]">Created At</th>
            <th class="px-4 py-2 w-20">CSV</th>
            <th class="px-4 py-2 w-[28rem]">Video Impressions Reports</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="report in reports"
            :key="report.id"
            @click="selectReport(report)"
            class="hover:bg-gray-100 cursor-pointer align-top"
          >
            <!-- Campaign -->
            <td class="border px-4 py-2 break-words">
              {{ report.campaign?.name || campaignIndex[report.campaign_uuid] || report.campaign_uuid }}
            </td>

            <!-- Filters -->
            <td class="border px-2 py-2 align-top">
              <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                <div class="min-w-0">
                  <div class="text-xs text-gray-500 mb-1">Countries</div>
                  <FilterCell :items="report.filters?.countries" :included="report.filters?.include_countries" />
                </div>
                <div class="min-w-0">
                  <div class="text-xs text-gray-500 mb-1">Runs</div>
                  <FilterCell :items="report.filters?.runs" :included="report.filters?.include_runs" />
                </div>
                <div class="min-w-0">
                  <div class="text-xs text-gray-500 mb-1">Games</div>
                  <FilterCell :items="report.filters?.games" :included="report.filters?.include_games" />
                </div>
              </div>
            </td>

            <!-- Status -->
            <td class="border px-3 py-2 align-top">
              <div
                class="text-sm leading-snug whitespace-normal break-words max-w-[8ch] sm:max-w-[10ch] md:max-w-[12ch] lg:max-w-[14ch]"
                :title="report.report_status"
              >
                {{ report.report_status }}
              </div>
            </td>

            <!-- Created -->
            <td class="border px-4 py-2 break-words">
              {{ report.created_at }}
            </td>

            <!-- CSV (Native Display) -->
            <td class="border px-2 py-2">
              <button
                class="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                :disabled="downloadingId === report.id || loading"
                @click.stop="downloadCsv(report)"
                title="Download CSV"
              >
                {{ downloadingId === report.id ? 'Downloading…' : 'CSV' }}
              </button>
            </td>

            <!-- Video Impressions -->
            <td class="border px-3 py-3 align-top" @click.stop>
              <div v-if="videoFlagsReady && isVideoCampaign[report.campaign_uuid] === false" class="text-sm text-gray-500">
                Not Video Campaign
              </div>

              <div v-else>
                <button
                  class="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                  :disabled="loading"
                  @click="openVideoModal(report)"
                >
                  Generate Video Impression Report
                </button>

                <div class="mt-3 overflow-x-auto" v-if="(report.video_impressions_reports || []).length">
                  <table class="min-w-full bg-white border">
                    <thead>
                      <tr class="text-left">
                        <th class="px-2 py-1 w-40">Creative</th>
                        <th class="px-2 py-1 w-40">Country</th>
                        <th class="px-2 py-1 w-32">Start</th>
                        <th class="px-2 py-1 w-32">End</th>
                        <th class="px-2 py-1 w-32">Status</th>
                        <th class="px-2 py-1 w-20">CSV</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="vr in report.video_impressions_reports"
                        :key="vr.id"
                        class="align-top hover:bg-gray-50"
                        @click="selectedVideoReport = vr"
                      >
                        <td class="border px-2 py-1">{{ vr.creative?.user_defined_name ?? vr.creative_uuid }}</td>
                        <td class="border px-2 py-1">{{ vr?.country?.name || 'All/None' }}</td>
                        <td class="border px-2 py-1">{{ vr.date_range?.start || '—' }}</td>
                        <td class="border px-2 py-1">{{ vr.date_range?.end || '—' }}</td>
                        <td class="border px-2 py-1">{{ vr.status || 'queued' }}</td>
                        <td class="border px-2 py-1">
                          <button
                            class="px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                            :disabled="downloadingVideoId === vr.id || loading"
                            @click.stop="downloadVideoCsv(vr)"
                            title="Download Video Report CSV"
                          >
                            {{ downloadingVideoId === vr.id ? '…' : 'CSV' }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-else class="mt-3 text-xs text-gray-500">No video reports yet.</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex justify-between mt-4">
      <button
        class="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        @click="prevPage"
        :disabled="page === 1 || loading"
      >Previous</button>

      <span class="px-4 py-2">Page {{ page }} of {{ totalPages }}</span>

      <button
        class="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        @click="nextPage"
        :disabled="page === totalPages || loading"
      >Next</button>
    </div>

    <!-- MODAL -->
    <VideoReportModal
      :open="modalOpen"
      :report="activeReport"
      :creatives="creativesByCampaign[activeReport?.campaign_uuid] || []"
      :countries="countriesByCampaign[activeReport?.campaign_uuid] || []"
      :creativeError="creativeLoadErrors[activeReport?.campaign_uuid]"
      :countryError="countryLoadErrors[activeReport?.campaign_uuid]"
      :prefillCountryUuid="prefillCountryUuid(activeReport)"
      :busy="generatingId === activeReport?.id"
      :error="modalError"
      @close="closeModal"
      @submit="submitVideoReport"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import NativeDisplayScheduledReportDisplay from '@/components/NativeDisplayScheduledReportDisplay.vue'
import VideoImpressionsReportDisplay from '@/components/VideoImpressionsReportDisplay.vue'
import FilterCell from '@/components/FilterCell.vue'
import VideoReportModal from '@/components/VideoReportModal.vue'

/* State */
const reports = ref([])
const selectedReport = ref(null)
const page = ref(1)
const totalPages = ref(1)
const loading = ref(false)
const error = ref('')
const downloadingId = ref(null)        // native display CSV
const downloadingVideoId = ref(null)   // video CSV
let pollTimer = 0
let selectedVideoReport = ref(null)
const campaignIndex = window?.campaignIndex || {}

/* Video flags / lookups */
const isVideoCampaign = ref({})
const videoFlagsReady = ref(false)

const creativesByCampaign = ref({})
const creativeLoadErrors = ref({})

const countriesByCampaign = ref({})
const countryLoadErrors = ref({})

/* Modal */
const modalOpen = ref(false)
const modalError = ref('')
const activeReport = ref(null)
const generatingId = ref(null)

/* Fetch reports */
const fetchReports = async (p = 1) => {
  if (loading.value) return
  loading.value = true
  try {
    const { data } = await axios.get('/native-display-reports', { params: { page: p } })
    reports.value = data.data || []
    page.value = data.current_page
    totalPages.value = data.last_page
    error.value = ''
    await fetchVideoFlagsFor(reports.value)
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to load reports.'
  } finally {
    loading.value = false
  }
}

const selectReport = (report) => { selectedReport.value = report }
const prevPage = () => page.value > 1 && fetchReports(page.value - 1)
const nextPage = () => page.value < totalPages.value && fetchReports(page.value + 1)

/* CSV: Native Display */
const getCsvUrl = (r) => `/native-display-reports/${r.id}/csv`
const getFilenameFromDisposition = (h) => {
  if (!h) return null
  const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(h)
  return decodeURIComponent(m?.[1] || m?.[2] || m?.[3] || '')
}
const downloadCsv = async (report) => {
  if (!report?.id) return
  try {
    downloadingId.value = report.id
    const res = await axios.get(getCsvUrl(report), { responseType: 'blob' })
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = getFilenameFromDisposition(res.headers['content-disposition']) || `report-${report.id}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to download CSV.'
  } finally {
    downloadingId.value = null
  }
}

/* CSV: Video Impressions */
const getVideoCsvUrl = (vr) => `/video-impressions-reports/${vr.id}/csv`
const downloadVideoCsv = async (vr) => {
  if (!vr?.id) return
  try {
    downloadingVideoId.value = vr.id
    const res = await axios.get(getVideoCsvUrl(vr), { responseType: 'blob' })
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = getFilenameFromDisposition(res.headers['content-disposition']) || `video-report-${vr.id}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to download Video CSV.'
  } finally {
    downloadingVideoId.value = null
  }
}

/* Helpers */
const chunk = (arr, size = 200) => {
  if (!Array.isArray(arr) || !arr.length) return []
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

const fetchVideoFlagsFor = async (items) => {
  const uuids = Array.from(new Set(items.map(r => r?.campaign_uuid).filter(Boolean)))
  if (!uuids.length) { videoFlagsReady.value = true; return }

  try {
    const chunks = chunk(uuids, 200)
    const results = await Promise.all(
      chunks.map(async part => {
        const { data } = await axios.post('/campaigns/is-video', { uuids: part })
        return data || {}
      })
    )
    isVideoCampaign.value = results.reduce((acc, cur) => Object.assign(acc, cur), {})
  } catch {
    isVideoCampaign.value = Object.fromEntries(uuids.map(u => [u, true]))
  } finally {
    videoFlagsReady.value = true
  }
}

/* Modal open/close */
const openVideoModal = async (report) => {
  activeReport.value = report
  modalError.value = ''

  await Promise.all([
    ensureCreatives(report.campaign_uuid),
    ensureCountries(report)
  ])

  modalOpen.value = true
}
const closeModal = () => { modalOpen.value = false; activeReport.value = null }

/* Data loaders */
const ensureCreatives = async (campaignUuid) => {
  if (!campaignUuid) return
  if (creativesByCampaign.value[campaignUuid]?.length) return
  try {
    const { data } = await axios.get(`/campaigns/${campaignUuid}/creatives`, { params: { video_only: 1 } })
    creativesByCampaign.value = { ...creativesByCampaign.value, [campaignUuid]: data?.data || [] }
    const errs = { ...creativeLoadErrors.value }; delete errs[campaignUuid]; creativeLoadErrors.value = errs
  } catch (e) {
    creativeLoadErrors.value = {
      ...creativeLoadErrors.value,
      [campaignUuid]: e.response?.data?.message || 'Failed to load creatives.'
    }
  }
}

const ensureCountries = async (report) => {
  const uuid = report?.campaign_uuid
  if (!uuid) return
  // Try API first (proper UUIDs); fall back to filters if API unavailable.
  try {
    const { data } = await axios.get(`/countries`)
    const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
    if (list.length) {
      countriesByCampaign.value = { ...countriesByCampaign.value, [uuid]: list }
      const errs = { ...countryLoadErrors.value }; delete errs[uuid]; countryLoadErrors.value = errs
      return
    }
  } catch (e) {
    countryLoadErrors.value = { ...countryLoadErrors.value, [uuid]: '' }
  }

  const fromFilters = normalizedCountries(report)
  countriesByCampaign.value = { ...countriesByCampaign.value, [uuid]: fromFilters }
  const errs = { ...countryLoadErrors.value }; delete errs[uuid]; countryLoadErrors.value = errs
}

const prefillCountryUuid = (report) => {
  const included = report?.filters?.include_countries !== false
  const list = Array.isArray(report?.filters?.countries) ? report.filters.countries : []
  if (!included) return null
  if (list.length === 1 && typeof list[0] === 'object' && list[0]?.uuid) return list[0].uuid
  if (list.length === 1 && typeof list[0] === 'string') return null
  return null
}

const normalizedCountries = (report) => {
  const included = report?.filters?.include_countries !== false
  if (!included) return []
  const items = Array.isArray(report?.filters?.countries) ? report.filters.countries : []
  return items
    .map(c => (typeof c === 'string' ? { uuid: undefined, name: c } : c))
    .filter(c => c && (c.uuid || c.name))
}

/* Submit (modal emits payload) */
const submitVideoReport = async (payload) => {
  if (!payload?.native_display_report_id || !payload?.creative_uuid) {
    modalError.value = 'Missing required fields.'
    return
  }
  if (payload.start_date && payload.end_date && payload.start_date > payload.end_date) {
    modalError.value = 'start_date must be on or before end_date'
    return
  }

  try {
    generatingId.value = payload.native_display_report_id
    const clean = { ...payload, native_display_report_id: payload.native_display_report_id }
    await axios.post('/video-impressions-reports/dispatch', clean)
    await fetchReports(page.value)
    closeModal()
  } catch (e) {
    modalError.value = e.response?.data?.message || 'Failed to queue video impressions report.'
  } finally {
    generatingId.value = null
  }
}

/* Mount + poll */
onMounted(() => {
  fetchReports(page.value)
  pollTimer = setInterval(() => fetchReports(page.value), 5000)
})
onUnmounted(() => clearInterval(pollTimer))
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>